/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useCallback } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native-unistyles';
import ButtonView, {
  ButtonViewType,
} from '@airasia-phoenix/react-native/components/ButtonView';
import TextView from '@airasia-phoenix/react-native/components/TextView';
import type { Chip } from '../../pages/SocialFeedPage/types';
import type { FooterProps } from './types';
import { getTextStyle, pd } from '@airasia-phoenix/react-native/utils';
import { useSocialAnalytics } from '../../analytics';

export const FooterView = ({ chips = [], onChipClick }: FooterProps) => {
  const { t } = useTranslation('social');
  const { trackFooterChipTap } = useSocialAnalytics();

  const handleChipPress = useCallback(
    (chip: Chip) => {
      if (onChipClick) {
        trackFooterChipTap(chip.chip_id, chip.location.country);
        onChipClick(chip.deeplink, chip.location);
      }
    },
    [onChipClick, trackFooterChipTap],
  );

  return (
    <View style={styles.container}>
      <TextView
        text={t('movement.chipsFooterTitle')}
        style={[
          getTextStyle(
            pd.sys.typescale.heading_4,
            pd.sys.colour.content_primary,
          ),
        ]}
      />
      {chips.length > 0 && (
        <View style={styles.chipsRow}>
          {chips.map((chip) => (
            <ButtonView
              key={chip.chip_id}
              type={ButtonViewType.SECONDARY_SMALL}
              text={chip.label}
              onPress={() => handleChipPress(chip)}
              disabled={!onChipClick}
            />
          ))}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create(() => ({
  container: {
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  chipsRow: {
    flexDirection: 'row' as const,
    flexWrap: 'wrap' as const,
    gap: 8,
    marginTop: 8,
  },
}));
